import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { formatDayYear, formatPeriod } from '../../lib/format/dates.ts';
import { answerState } from '../../lib/requests/answer.ts';
import { ApiFailure, api, type RequestPeriod, type RequestStatus } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { ScrollRegion } from '../components/ScrollRegion.tsx';
import { copy } from '../copy/en-CA.ts';
import { useSession } from '../session.tsx';

const c = copy.requests;

/** The request periods a physician can submit to, open ones first. */
export function RequestsPage() {
  const { user } = useSession();
  const [periods, setPeriods] = useState<RequestPeriod[] | null>(null);
  const [mine, setMine] = useState<Record<string, number>>({});
  const [byStatus, setByStatus] = useState<Record<string, Record<RequestStatus, number>>>({});
  const [answered, setAnswered] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .requestPeriods()
      .then((r) => {
        setPeriods([...r.periods].sort((a, b) => Number(b.open) - Number(a.open)));
        setMine(r.mine);
        setByStatus(r.mineByStatus);
        setAnswered(r.answered);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
      });
  }, []);

  const isPhysician = Boolean(user?.physicianId);
  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{isPhysician ? c.intro : c.notPhysician}</p>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
      {periods?.length === 0 ? (
        <section className="card">
          <p className="empty">{c.empty}</p>
        </section>
      ) : null}
      {periods && periods.length > 0 ? (
        <ScrollRegion label={c.title}>
          <table>
            <thead>
              <tr>
                <th>{c.columns.period}</th>
                <th>{c.columns.due}</th>
                <th>{c.columns.status}</th>
                {isPhysician ? <th>{c.columns.mine}</th> : null}
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link to={`/requests/${p.id}`}>
                      <strong>{p.name}</strong>
                    </Link>
                    <div className="muted small">{formatPeriod(p.startDate, p.endDate)}</div>
                  </td>
                  <td>{formatDayYear(p.dueDate)}</td>
                  <td>{p.open ? <span className="badge physician">{c.open}</span> : <span className="badge inactive">{c.closed}</span>}</td>
                  {isPhysician ? (
                    <td>
                      {/* "Nothing needed this time" is an answer, not "0 submitted". */}
                      {answerState(mine[p.id] ?? 0, answered[p.id]) === 'nothing-needed'
                        ? c.nothingNeeded.list
                        : c.submittedDetail(mine[p.id] ?? 0, byStatus[p.id] ?? { pending: 0, approved: 0, declined: 0 })}{' '}
                      <Link className="btn small" to={`/requests/${p.id}`} style={{ marginLeft: '0.5rem' }}>
                        {p.open ? c.edit : c.view}
                      </Link>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollRegion>
      ) : null}
    </>
  );
}
